import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  Theme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AreaInfo } from "IF/interfaceType";
import { UserRole } from "IF/userSession";
import { useThemeMode } from "Contexts/ThemeContext";

interface MobileAreaProps {
  row: AreaInfo;
  originalRow: AreaInfo;
  index: number;
  size: number;
  width: number;
  userRole: string;
  handleTextChange: (
    id: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleSortChange: (id: number, value: number) => void;
  handleDelete: (index: number) => void;
  handleConfirmModalOpen: (
    message: React.ReactNode,
    confirmAction: () => void
  ) => void;
  openColorPicker: (simId: string) => void;
}

const SettingAreaMobile: React.FC<MobileAreaProps> = ({
  row,
  originalRow,
  index,
  size,
  width,
  userRole,
  handleDelete,
  handleTextChange,
  handleSortChange,
  handleConfirmModalOpen,
  openColorPicker,
}) => {
  const rowHeight = "20px";
  const theme = useTheme();
  const { mode } = useThemeMode();

  return (
    <Card
      sx={{
        bgcolor: row.use ? theme.palette.custom.row : "#707070",
        gap: 1,
        maxWidth: "100%",
      }}
    >
      <CardContent>
        <TableContainer
          sx={{
            overflowY: "hidden",
          }}
        >
          <Table>
            <TableBody>
              <TableRow
                sx={{
                  boxShadow: `inset 0 0 0 0.2px ${
                    row.use
                      ? theme.palette.custom.paperBorderColorSub
                      : "#808080"
                  }`,
                }}
              >
                <TableCell
                  scope="row"
                  colSpan={2}
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    p: 1,
                    height: rowHeight,
                    lineHeight: rowHeight,
                  }}
                >
                  {originalRow.name}
                </TableCell>
              </TableRow>
              {/* SIMID */}
              <TableRow
                sx={{
                  boxShadow: `inset 0 0 0 0.2px ${
                    row.use
                      ? theme.palette.custom.paperBorderColorSub
                      : "#808080"
                  }`,
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                    height: rowHeight,
                    lineHeight: rowHeight,
                  }}
                >
                  IMSI
                </TableCell>
                <TableCell
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    width: "60%",
                    padding: "0px",
                    height: rowHeight,
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    value={row.simId}
                    disabled
                    fullWidth
                    sx={{ color: "primary" }}
                    InputProps={{
                      sx: {
                        height: rowHeight,
                        fontSize: "0.8rem",
                        paddingY: "4px",
                        color: "primary",
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor:
                            mode === "dark" ? "white" : "black",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
              {/* シリアルNo */}
              <TableRow
                sx={{
                  boxShadow: `inset 0 0 0 0.2px ${
                    row.use
                      ? theme.palette.custom.paperBorderColorSub
                      : "#808080"
                  }`,
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                    height: rowHeight,
                    lineHeight: rowHeight,
                  }}
                >
                  シリアルNo
                </TableCell>
                <TableCell
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    color: "white",
                    width: "60%",
                    padding: "0px",
                    height: rowHeight,
                  }}
                >
                  <Box sx={{ p: 0.5 }}>
                    {(userRole === UserRole.YKC ||
                      userRole === UserRole.RENTAL) &&
                    row.use ? (
                      <TextField
                        variant="outlined"
                        size="small"
                        value={row.serialNo}
                        fullWidth
                        name="serialNo"
                        inputProps={{ maxLength: 6 }}
                        onChange={(e) => handleTextChange(row.id, e)}
                        InputProps={{
                          sx: {
                            width: "calc(100% - 2px)",
                            height: "26px",
                            fontSize: "0.8rem",
                            paddingY: "4px",
                            backgroundColor: "background.default",
                          },
                        }}
                      />
                    ) : (
                      <TextField
                        variant="outlined"
                        size="small"
                        value={row.serialNo}
                        fullWidth
                        disabled
                        InputProps={{
                          sx: {
                            width: "calc(100% - 2px)",
                            height: "26px",
                            fontSize: "0.8rem",
                            paddingY: "4px",
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor:
                                mode === "dark" ? "white" : "black",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          },
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              {/* 磁界ID */}
              <TableRow
                sx={{
                  boxShadow: `inset 0 0 0 0.2px ${
                    row.use
                      ? theme.palette.custom.paperBorderColorSub
                      : "#808080"
                  }`,
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                    height: rowHeight,
                    lineHeight: rowHeight,
                  }}
                >
                  磁界ID
                </TableCell>
                <TableCell
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    color: "white",
                    width: "60%",
                    padding: "0px",
                    height: rowHeight,
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    value={row.magId}
                    disabled
                    fullWidth
                    InputProps={{
                      sx: {
                        height: rowHeight,
                        fontSize: "0.8rem",
                        paddingY: "4px",
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor:
                            mode === "dark" ? "white" : "black",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
              {/* エリア名 */}
              <TableRow
                sx={{
                  boxShadow: `inset 0 0 0 0.2px ${
                    row.use
                      ? theme.palette.custom.paperBorderColorSub
                      : "#808080"
                  }`,
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                    height: rowHeight,
                    lineHeight: rowHeight,
                  }}
                >
                  エリア名
                </TableCell>
                <TableCell
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    color: "white",
                    width: "60%",
                    padding: "0px",
                    height: rowHeight,
                  }}
                >
                  <Box sx={{ p: 0.5 }}>
                    {row.use ? (
                      <TextField
                        variant="outlined"
                        size="small"
                        value={row.name}
                        name="name"
                        onChange={(e) => handleTextChange(row.id, e)}
                        fullWidth
                        InputProps={{
                          sx: {
                            backgroundColor: "background.default",
                            fontSize: "0.8rem",
                            width: "calc(100% - 2px)",
                            height: "26px",
                          },
                        }}
                      />
                    ) : (
                      <TextField
                        variant="outlined"
                        size="small"
                        value={row.name}
                        fullWidth
                        disabled
                        InputProps={{
                          sx: {
                            width: "calc(100% - 2px)",
                            height: "26px",
                            fontSize: "0.8rem",
                            paddingY: "4px",
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor:
                                mode === "dark" ? "white" : "black",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          },
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              {/* 表示順 */}
              <TableRow
                sx={{
                  boxShadow: `inset 0 0 0 0.2px ${
                    row.use
                      ? theme.palette.custom.paperBorderColorSub
                      : "#808080"
                  }`,
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                    height: rowHeight,
                    lineHeight: rowHeight,
                  }}
                >
                  表示順
                </TableCell>
                <TableCell
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    color: "white",
                    width: "60%",
                    padding: "0px",
                    height: rowHeight,
                  }}
                >
                  <Box sx={{ p: 0.5 }}>
                    {row.use ? (
                      <Select
                        name="sortBy"
                        value={row.sortBy}
                        size="small"
                        fullWidth
                        onChange={(e) =>
                          handleSortChange(
                            row.id,
                            parseInt(e.target.value as string, 10)
                          )
                        }
                        sx={{
                          backgroundColor: "background.default",
                          textAlign: "left",
                          width: "calc(100% - 2px)",
                          height: "26px",
                        }}
                      >
                        {Array.from({ length: size }, (_, i) => (
                          <MenuItem
                            key={"mobile" + i + 1}
                            value={i + 1}
                            sx={{ height: "26px" }}
                          >
                            {i + 1}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <TextField
                        variant="outlined"
                        size="small"
                        value={"ー"}
                        disabled
                        fullWidth
                        InputProps={{
                          sx: {
                            height: rowHeight,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor:
                                mode === "dark" ? "white" : "black",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          },
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              {/* 色選択 */}
              <TableRow
                sx={{
                  boxShadow: `inset 0 0 0 0.2px ${
                    row.use
                      ? theme.palette.custom.paperBorderColorSub
                      : "#808080"
                  }`,
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                    height: rowHeight,
                    lineHeight: rowHeight,
                    boxShadow: `inset 0 0 0 0.2px ${
                      row.use
                        ? theme.palette.custom.paperBorderColorSub
                        : "#808080"
                    }`,
                  }}
                >
                  色選択
                </TableCell>
                <TableCell
                  sx={{
                    padding: "0px",
                    marginTop: "0px",
                    marginBottom: "0px",
                    alignItems: "flex-start",
                  }}
                >
                  <IconButton
                    disabled={!row.use}
                    onClick={() => openColorPicker(row.simId)}
                  >
                    <Box
                      sx={{
                        width: "22px",
                        height: "22px",
                        backgroundColor: row.colorCode,
                        border: "1px solid #000",
                        borderRadius: "4px",
                      }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {(userRole === UserRole.YKC || userRole === UserRole.RENTAL) && (
          <Box
            sx={{
              textAlign: "end",
              verticalAlign: "middle",
              paddingTop: "5px",
            }}
          >
            {row.use ? (
              <IconButton
                onClick={() =>
                  handleConfirmModalOpen(
                    <Box>
                      選択したエリアを削除します。よろしいですか？
                      <br />
                      <span style={{ color: "red" }}>
                        ※削除したエリアは編集ができなくなります。
                      </span>
                    </Box>,
                    () => handleDelete(index)
                  )
                }
              >
                <DeleteIcon sx={{ fontSize: 24 }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: 18, // DeleteIcon と同じサイズに設定
                    ml: 0.5,
                  }}
                >
                  削除
                </Typography>
              </IconButton>
            ) : (
              <></>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SettingAreaMobile;
